import { useNavigate, useNavigationType } from 'react-router-dom';
import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSortableTable } from '@/components/shared/SortableTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import { SocialProfilesEditor } from '@/components/positions/SocialProfilesEditor';
import { DocumentFieldsEditor } from '@/components/positions/DocumentFieldsEditor';
import { Switch } from '@/components/ui/switch';
import { Plus, MoreHorizontal, Pencil, Trash2, Sparkles, Send, X, Check, XCircle, Wand2, Loader2, ExternalLink, Copy, CheckCircle2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PositionStatusBadge } from '@/components/shared/Badges';
import { ResponsiveTable, TABLE_COL, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { statusCellClass } from '@/components/ui/responsive-table/columnLayout';
import { useLanguage } from '@/contexts/LanguageContext';
import { ApplicationsListFiltersToolbar } from '@/components/applications/ApplicationsListFiltersToolbar';
import { useViewportLockedListPage } from '@/hooks/use-mobile';
import { NoCompanyEmptyState } from '@/components/shared/NoCompanyEmptyState';
import { formatRelative } from '@/lib/dateUtils';
import {
  jobPositionAiColumnClass,
  jobPositionDialogBodyClass,
  jobPositionDialogContentClass,
  jobPositionFormColumnClass,
  jobPositionFormScrollClass,
} from '@/lib/jobPositionDialogClasses';
import { cn } from '@/lib/utils';
import type { Seniority, ContractType, WorkMode } from '@/types';
import { useJobPositions } from '@/hooks/useJobPositions';
import { normalizeJobLocation } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { ListPagination } from '@/components/shared/ListPagination';
import { ListTablePanel } from '@/components/shared/ListTablePanel';
import {
  isListViewLandingAfterReload,
  listViewStorageKey,
  loadSessionJson,
  type JobPositionsListPersist,
} from '@/lib/listViewPersistence';
import { usePersistedListView } from '@/hooks/usePersistedListView';
import { useTrialWriteGuard } from '@/hooks/useTrialWriteAccess';
import {
  POSITION_CONTRACT_VALUES,
  POSITION_SENIORITY_VALUES,
  POSITION_WORK_MODE_VALUES,
  positionContractLabel,
  positionSeniorityLabel,
  positionWorkModeLabel,
} from '@/lib/positionFormLabels';

const JobPositionsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { selectedCompany } = useAuth();
  const hook = useJobPositions();
  const viewportLocked = useViewportLockedListPage();
  const { guardWrite, writeBlocked } = useTrialWriteGuard();
  const positionsListKey = selectedCompany
    ? listViewStorageKey('job-positions', selectedCompany.id)
    : undefined;
  const savedPositionsList = positionsListKey && !isListViewLandingAfterReload(navigationType)
    ? loadSessionJson<JobPositionsListPersist>(positionsListKey)
    : null;

  const rowsWithTeam = useMemo(
    () =>
      hook.paginatedFiltered.map(p => ({
        ...p,
        teamName: p.team
          ? hook.companyTeams.find(ct => ct.id === p.team)?.name ?? p.team
          : t('positions.noTeam'),
      })),
    [hook.paginatedFiltered, hook.companyTeams, t],
  );

  const { sorted, sort, toggleSort } = useSortableTable(
    rowsWithTeam,
    savedPositionsList
      ? { column: savedPositionsList.sortColumn, direction: savedPositionsList.sortDirection }
      : { column: 'createdAt', direction: 'desc' },
  );

  usePersistedListView(positionsListKey, {
    statusFilter: hook.statusFilter,
    teamFilter: hook.teamFilter,
    search: hook.search,
    page: hook.page,
    sortColumn: sort.column ?? 'createdAt',
    sortDirection: (sort.direction ?? 'desc') as 'asc' | 'desc',
  });

  type PositionRow = (typeof rowsWithTeam)[number];

  const positionsColumns: ResponsiveTableColumn<PositionRow>[] = [
    {
      id: 'title',
      label: t('positions.title'),
      priority: 'primary',
      alwaysVisible: true,
      sortable: true,
      ...TABLE_COL.title,
      truncate: true,
      render: (row) => <span className="font-medium truncate block" title={row.title}>{row.title}</span>,
    },
    {
      id: 'teamName',
      label: t('positions.team'),
      priority: 'secondary',
      sortable: true,
      ...TABLE_COL.text,
      truncate: true,
      render: (row) => (
        <span className="text-muted-foreground truncate block" title={row.teamName}>{row.teamName}</span>
      ),
    },
    {
      id: 'status',
      label: t('positions.status'),
      priority: 'primary',
      sortable: true,
      alwaysVisible: true,
      ...TABLE_COL.status,
      render: (row) => (
        <div className={statusCellClass()}>
          <PositionStatusBadge status={row.status} />
        </div>
      ),
    },
    {
      id: 'applicationsCount',
      label: t('positions.apps'),
      priority: 'secondary',
      sortable: true,
      ...TABLE_COL.count,
      render: (row) => <span className="font-medium tabular-nums">{row.applicationsCount}</span>,
    },
    {
      id: 'postings',
      label: t('positions.postings'),
      priority: 'tertiary',
      sortable: true,
      sortAccessor: (row) => hook.postingsCountMap[row.id] ?? 0,
      ...TABLE_COL.count,
      render: (row) => (
        <span
          className="text-muted-foreground tabular-nums hover:text-primary cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/job-positions/${row.id}?tab=postings`);
          }}
        >
          {hook.postingsCountMap[row.id] || 0}
        </span>
      ),
    },
    {
      id: 'createdAt',
      label: t('positions.created'),
      priority: 'tertiary',
      sortable: true,
      hideOn: 'tablet',
      ...TABLE_COL.date,
      defaultVisible: false,
      render: (row) => (
        <span className="text-muted-foreground">{formatRelative(row.createdAt)}</span>
      ),
    },
    {
      id: 'updatedAt',
      label: t('positions.updated'),
      priority: 'tertiary',
      sortable: true,
      hideOn: 'tablet',
      ...TABLE_COL.date,
      render: (row) => (
        <span className="text-muted-foreground">{formatRelative(row.updatedAt)}</span>
      ),
    },
    {
      id: 'actions',
      label: '',
      priority: 'hidden',
      alwaysVisible: true,
      ...TABLE_COL.actions,
      render: (row) => (
        <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!guardWrite()) return;
                  if (!selectedCompany) return;
                  try {
                    const full = await api.positions.get(selectedCompany.id, row.id);
                    if (full) {
                      hook.setEditData(full);
                      hook.setDialogOpen(true);
                    }
                  } catch {
                    toast.error(t('positions.loadFailed'));
                  }
                }}
              >
                <Pencil className="h-3.5 w-3.5 mr-2" /> {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                if (!guardWrite()) return;
                void hook.handleStatusToggle(row);
              }}>
                {row.status === 'active' ? <XCircle className="h-3.5 w-3.5 mr-2" /> : <Check className="h-3.5 w-3.5 mr-2" />}
                {row.status === 'active' ? t('positions.closePosition') : t('positions.activatePosition')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                if (!guardWrite()) return;
                hook.setDeleteId(row.id);
              }} className="text-destructive">
                <Trash2 className="h-3.5 w-3.5 mr-2" /> {t('positions.deletePosition')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  // Pre-onboarding short-circuit: when the recruiter has no company yet
  // there is no useful UI to show — every action below requires a
  // companyId. Render the unified empty-state with a CTA that takes
  // them through onboarding instead of an empty filtered table.
  if (!selectedCompany) {
    return (
      <div className="space-y-4">
        <NoCompanyEmptyState body={t('emptyState.noCompany.body.positions')} />
      </div>
    );
  }
  const hasActiveListFilters =
    hook.statusFilter !== 'all'
    || hook.teamFilter !== 'all'
    || hook.search.trim().length > 0;

  return (
    <div
      className={cn(
        viewportLocked
          ? 'flex min-h-0 flex-1 flex-col gap-4 overflow-hidden'
          : 'space-y-4',
      )}
    >
      <ApplicationsListFiltersToolbar
        search={{
          value: hook.search,
          onChange: hook.setSearch,
          placeholder: t('positions.searchPositions'),
        }}
        status={{
          value: hook.statusFilter,
          onChange: hook.setStatusFilter,
          active: hook.statusFilter !== 'all',
          options: [
            { value: 'all', label: t('positions.allStatus') },
            { value: 'draft', label: t('positions.draft') },
            { value: 'active', label: t('positions.active') },
            { value: 'closed', label: t('positions.closed') },
            { value: 'cancelled', label: t('positions.cancelled') },
          ],
        }}
        team={{
          value: hook.teamFilter,
          onChange: hook.setTeamFilter,
          active: hook.teamFilter !== 'all',
          placeholder: t('positions.allTeams'),
          options: [
            { value: 'all', label: t('positions.allTeams') },
            { value: 'unassigned', label: t('positions.noTeam') },
            ...hook.companyTeams.map((team) => ({
              value: team.id,
              label: team.name,
            })),
          ],
        }}
        primaryAction={{
          label: t('positions.newPosition'),
          disabled: writeBlocked,
          onClick: () => {
            if (!guardWrite()) return;
            hook.openNewPosition();
          },
          icon: Plus,
        }}
        showClearFilters={hasActiveListFilters}
        onClearFilters={() => {
          hook.setStatusFilter('all');
          hook.setTeamFilter('all');
          hook.setSearch('');
        }}
        clearFiltersLabel={t('applications.clearFilters')}
      />

      {/* Table */}
      <ListTablePanel
        scrollableBody={viewportLocked}
        fillViewport={viewportLocked}
        className={viewportLocked ? 'flex-1 min-h-0' : undefined}
        showFooter={hook.totalFiltered > 0}
        footer={
          <ListPagination
            page={hook.page}
            totalPages={hook.totalPages}
            totalItems={hook.totalFiltered}
            onPageChange={hook.setPage}
            disabled={hook.loading}
          />
        }
      >
        <ResponsiveTable
            scrollBodyOnly={viewportLocked}
            className={viewportLocked ? 'h-full min-h-0' : undefined}
            data={sorted}
            columns={positionsColumns}
            getRowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/job-positions/${row.id}`)}
            storageKey="positions-list"
            sort={sort}
            onSortChange={toggleSort}
            isLoading={hook.loading && sorted.length === 0}
            emptyState={
              <p className="text-sm text-muted-foreground">{t('positions.noPositions')}</p>
            }
            ariaLabel={t('positions.title')}
        />
      </ListTablePanel>

      {/* Create/Edit Dialog */}
      <Dialog open={hook.dialogOpen} onOpenChange={(open) => { hook.setDialogOpen(open); if (!open) { hook.setAiChatOpen(false); hook.setMemberSearch(''); } }}>
        <DialogContent className={jobPositionDialogContentClass({
          aiChatOpen: hook.aiChatOpen,
          wizardCreated: hook.wizardStep === 'created',
        })}>
          {/* Sprint 3 (S3.E) — post-create wizard step. Same dialog frame
              switches content so the recruiter doesn't see a flicker between
              "save" and "what's next?". */}
          {hook.wizardStep === 'created' && hook.createdPosition ? (
            <div className="p-6 space-y-5 min-w-0">
              <DialogHeader className="space-y-2 text-left">
                <div className="flex items-center gap-2 min-w-0">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                  <DialogTitle className="text-base">{t('positions.wizardCreatedTitle')}</DialogTitle>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('positions.wizardCreatedSubtitle')}
                </p>
              </DialogHeader>

              <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-2 min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  {t('positions.wizardLandingLabel')}
                </p>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <code className="min-w-0 w-full break-all text-xs font-mono bg-background border border-border/50 rounded px-2 py-1.5">
                    {hook.createdPosition.landingPageUrl || '—'}
                  </code>
                  {hook.createdPosition.landingPageUrl && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1 text-xs shrink-0 w-full sm:w-auto"
                      onClick={() => { void hook.copyCreatedPositionLink(); }}
                      aria-label={t('positions.copy')}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {t('positions.copy')}
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t('positions.wizardLandingHint')}
                </p>
              </div>

              <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!guardWrite()) return;
                    hook.handleSaveAsDraft();
                  }}
                  disabled={hook.activatingNow}
                  className="w-full sm:w-auto whitespace-nowrap"
                >
                  {t('positions.wizardSaveAsDraft')}
                </Button>
                <Button
                  size="sm"
                  className="gap-1.5 w-full sm:w-auto whitespace-nowrap"
                  onClick={() => { void hook.handleActivateAndOpen(); }}
                  disabled={hook.activatingNow}
                >
                  {hook.activatingNow ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                  {t('positions.wizardActivate')}
                </Button>
              </DialogFooter>
            </div>
          ) : (
          <div className={jobPositionDialogBodyClass}>
            {/* Form side — hidden on mobile when AI chat is open */}
            <div className={jobPositionFormColumnClass(hook.aiChatOpen)}>
              {/* Header */}
              <div className="shrink-0 flex items-center justify-between px-6 pt-6 pb-4 border-b border-border/40">
                <DialogTitle className="text-base">{hook.editData.id ? t('positions.editPosition') : t('positions.newPosition')}</DialogTitle>
                <div className="flex items-center gap-2 pr-6">
                  <Button
                    variant={hook.aiChatOpen ? 'default' : 'outline'}
                    size="sm"
                    className="gap-1.5 text-xs h-8"
                    onClick={() => hook.setAiChatOpen(!hook.aiChatOpen)}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    {hook.aiChatOpen ? t('positions.closeAI') : t('positions.aiAssistant')}
                  </Button>
                </div>
              </div>

              {/* Scrollable form */}
              <div className={jobPositionFormScrollClass}>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{t('positions.title')} *</Label>
                    <Input value={hook.editData.title || ''} onChange={e => hook.setEditData(d => ({ ...d, title: e.target.value }))} placeholder={t('positions.titleExample')} className="h-9" />
                  </div>
                   <div className="space-y-1.5">
                     <Label className="text-xs font-medium">{t('positions.description')} *</Label>
                     {/* Sprint 3 (S3.D): replaced the Tabs(write/preview) +
                         <Textarea>+<ReactMarkdown> split with a single
                         live-preview WYSIWYG. The editor exposes only the
                         formatting recruiters need (headings, bold, lists,
                         links, quotes); markdown source is still the source
                         of truth so the BE / public landing page render
                         identically to the in-form preview. */}
                     <MarkdownEditor
                       value={hook.editData.description || ''}
                       onChange={(next) => hook.setEditData(d => ({ ...d, description: next }))}
                       placeholder={t('positions.descriptionPlaceholder')}
                       ariaLabel={t('positions.description')}
                     />
                   </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.seniority')}</Label>
                      {hook.editData.seniority !== undefined && !['Junior', 'Junior-Mid', 'Mid', 'Mid-Senior', 'Senior', '', undefined].includes(hook.editData.seniority) || hook.editData.seniority === '__other__' ? (
                        <div className="flex gap-1.5">
                          <Input autoFocus value={hook.editData.seniority === '__other__' ? '' : (hook.editData.seniority || '')} onChange={e => hook.setEditData(d => ({ ...d, seniority: e.target.value || '__other__' }))} className="h-9" placeholder={t('positions.customSeniority')} />
                          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => hook.setEditData(d => ({ ...d, seniority: undefined }))}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <Select value={hook.editData.seniority || ''} onValueChange={v => hook.setEditData(d => ({ ...d, seniority: v === '__other__' ? '__other__' : v as Seniority }))}>
                          <SelectTrigger className="h-9"><SelectValue placeholder={t('positions.selectPlaceholder')} /></SelectTrigger>
                          <SelectContent>
                            {POSITION_SENIORITY_VALUES.map(s => (
                              <SelectItem key={s} value={s}>{positionSeniorityLabel(t, s)}</SelectItem>
                            ))}
                            <SelectItem value="__other__">{t('positions.other')}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.contractType')}</Label>
                      {hook.editData.contractType !== undefined && !['Full-time', 'Part-time', 'Freelance', 'Internship', 'Temporary', '', undefined].includes(hook.editData.contractType) || hook.editData.contractType === '__other__' ? (
                        <div className="flex gap-1.5">
                          <Input autoFocus value={hook.editData.contractType === '__other__' ? '' : (hook.editData.contractType || '')} onChange={e => hook.setEditData(d => ({ ...d, contractType: e.target.value || '__other__' }))} className="h-9" placeholder={t('positions.customContractType')} />
                          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => hook.setEditData(d => ({ ...d, contractType: undefined }))}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <Select value={hook.editData.contractType || ''} onValueChange={v => hook.setEditData(d => ({ ...d, contractType: v === '__other__' ? '__other__' : v as ContractType }))}>
                          <SelectTrigger className="h-9"><SelectValue placeholder={t('positions.selectPlaceholder')} /></SelectTrigger>
                          <SelectContent>
                            {POSITION_CONTRACT_VALUES.map(s => (
                              <SelectItem key={s} value={s}>{positionContractLabel(t, s)}</SelectItem>
                            ))}
                            <SelectItem value="__other__">{t('positions.other')}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.workMode')}</Label>
                      <Select value={hook.editData.workMode || ''} onValueChange={v => hook.setEditData(d => ({ ...d, workMode: v as WorkMode }))}>
                        <SelectTrigger className="h-9"><SelectValue placeholder={t('positions.selectPlaceholder')} /></SelectTrigger>
                        <SelectContent>
                          {POSITION_WORK_MODE_VALUES.map(s => (
                            <SelectItem key={s} value={s}>{positionWorkModeLabel(t, s)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.location')}</Label>
                      <Select value={hook.editData.location || ''} onValueChange={v => hook.setEditData(d => ({ ...d, location: v }))}>
                        <SelectTrigger className="h-9"><SelectValue placeholder={t('positions.selectLocation')} /></SelectTrigger>
                        <SelectContent>
                          {hook.companyOffices.length === 0 ? (
                            <SelectItem value="__none__" disabled>
                              {t('positions.noCompanyLocations')}
                            </SelectItem>
                          ) : (
                            hook.companyOffices.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Salary */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.salaryMin')}</Label>
                      <Input type="number" value={hook.editData.salaryMin || ''} onChange={e => hook.setEditData(d => ({ ...d, salaryMin: Number(e.target.value) }))} className="h-9" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.salaryMax')}</Label>
                      <Input type="number" value={hook.editData.salaryMax || ''} onChange={e => hook.setEditData(d => ({ ...d, salaryMax: Number(e.target.value) }))} className="h-9" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t('positions.currency')}</Label>
                      <Select value={hook.editData.salaryCurrency || 'EUR'} onValueChange={v => hook.setEditData(d => ({ ...d, salaryCurrency: v }))}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="GBP">GBP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Sprint 3 (S3.B): Auto-reject threshold field hidden.
                      The control was kept disabled with a "TODO" badge for
                      months — that's noise on the form and confuses recruiters
                      who try to set a value. The wiring (DTO + service) is
                      not in place yet, so removing the visible field here
                      doesn't lose any data; when we ship the feature we will
                      restore the input + label in the same slot. */}

                  {/* Candidate locale — screening/reports, emails, public posting. */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{t('positions.outputLanguage')}</Label>
                    <Select
                      value={
                        hook.editData.outputLanguage
                        ?? hook.selectedCompany?.defaultOutputLanguage
                        ?? 'it'
                      }
                      onValueChange={v =>
                        hook.setEditData(d => ({ ...d, outputLanguage: v as 'it' | 'en' }))
                      }
                    >
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="it">{t('positions.outputLanguageIt')}</SelectItem>
                        <SelectItem value="en">{t('positions.outputLanguageEn')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">
                      {t('positions.outputLanguageHint')}
                    </p>
                  </div>

                  <div className="space-y-2.5 pt-1">
                    <DocumentFieldsEditor
                      value={hook.editData.documents ?? []}
                      onChange={(documents) => hook.setEditData((d) => ({ ...d, documents }))}
                    />
                    <SocialProfilesEditor
                      value={hook.editData.socialProfiles ?? []}
                      onChange={(socialProfiles) => hook.setEditData((d) => ({ ...d, socialProfiles }))}
                    />
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">{t('positions.requirePhone')}</Label>
                      <Switch checked={hook.editData.requirePhone} onCheckedChange={v => hook.setEditData(d => ({ ...d, requirePhone: v }))} />
                    </div>
                    {/* Notify candidate toggle — hidden for now, hardcoded to false
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">{t('positions.notifyCandidate')}</Label>
                      <Switch checked={hook.editData.notifyCandidate} onCheckedChange={v => hook.setEditData(d => ({ ...d, notifyCandidate: v }))} />
                    </div>
                    */}
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-0.5">
                        <Label className="text-xs">{t('positions.allowDuplicateApplications')}</Label>
                        <p className="text-[11px] text-muted-foreground">{t('positions.allowDuplicateApplicationsHint')}</p>
                      </div>
                      <Switch checked={hook.editData.allowDuplicateApplications} onCheckedChange={v => hook.setEditData(d => ({ ...d, allowDuplicateApplications: v }))} />
                    </div>
                  </div>

                  {/* Team */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{t('positions.team')}</Label>
                    <Select value={hook.editData.team || ''} onValueChange={v => {
                      const newTeamMemberIds = hook.allMembers.filter(m => m.teamIds?.includes(v)).map(m => m.userId);
                      const currentIds = hook.editData.assignedMembers || [];
                      const manualIds = currentIds.filter(id => !hook.teamAutoMembers.includes(id) || id === hook.creatorUserId);
                      const newIds = [...new Set([...manualIds, ...newTeamMemberIds])];
                      hook.setTeamAutoMembers(newTeamMemberIds);
                      hook.setEditData(d => ({ ...d, team: v, assignedMembers: newIds }));
                    }}>
                      <SelectTrigger className="h-9"><SelectValue placeholder={t('positions.selectTeam')} /></SelectTrigger>
                      <SelectContent>
                        {hook.companyTeams.length === 0 ? (
                          <SelectItem value="__none__" disabled>
                            {t('positions.noCompanyTeams')}
                          </SelectItem>
                        ) : (
                          hook.companyTeams.map(team => (
                            <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Assigned Members */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{t('positions.assignedMembers')}</Label>
                    <div className="space-y-1.5">
                      {(hook.editData.assignedMembers || []).map(uid => {
                        const m = hook.allMembers.find(x => x.userId === uid);
                        if (!m) return null;
                        const isCreator = uid === hook.creatorUserId;
                        return (
                          <div key={uid} className="flex items-center justify-between bg-muted/30 rounded-md px-2.5 py-1.5">
                            <div className="flex items-center gap-2">
                              <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary">{m.displayName[0]}</div>
                              <span className="text-xs font-medium">{m.displayName}</span>
                              <span className="text-[10px] text-muted-foreground">{m.email}</span>
                              <span className="text-[10px] text-muted-foreground">{m.jobRole?.replace('_', ' ')}</span>
                              {/* Sprint 7+ follow-up (20/05/26): show the
                                  team NAME, not the raw teamId UUID. The
                                  previous render leaked Firestore doc ids
                                  into the chip ("· I2p3LOdKg5Can6YAKUCH")
                                  which looked like a bug to recruiters.
                                  When the team is unknown (just-deleted /
                                  not yet loaded) we omit the chip entirely
                                  rather than fall back to the UUID. */}
                              {(() => {
                                const tid = m.teamIds?.[0];
                                if (!tid) return null;
                                const team = hook.companyTeams.find(team => team.id === tid);
                                if (!team) return null;
                                return <span className="text-[10px] text-muted-foreground">· {team.name}</span>;
                              })()}
                              {isCreator && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">{t('positions.assignedMembersCreator')}</span>}
                            </div>
                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => hook.setEditData(d => ({ ...d, assignedMembers: (d.assignedMembers || []).filter(id => id !== uid) }))}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        );
                      })}
                      {(() => {
                        const available = hook.allMembers.filter(m => !(hook.editData.assignedMembers || []).includes(m.userId));
                        if (available.length === 0) return <p className="text-[11px] text-muted-foreground">{t('positions.assignedMembersAllAssigned')}</p>;
                        return (
                          <div className="relative">
                            <Input
                              value={hook.memberSearch}
                              onChange={e => hook.setMemberSearch(e.target.value)}
                              placeholder={t('positions.assignedMembersSearch')}
                              className="h-8 text-xs"
                            />
                            {hook.memberSearch.trim() && (
                              <div className="absolute z-10 mt-1 w-full bg-popover border border-border rounded-md shadow-md max-h-32 overflow-y-auto">
                                {available.filter(m =>
                                  m.displayName.toLowerCase().includes(hook.memberSearch.toLowerCase()) ||
                                  m.email.toLowerCase().includes(hook.memberSearch.toLowerCase())
                                ).map(m => (
                                  <button
                                    key={m.userId}
                                    className="flex items-center gap-2 w-full text-left px-2.5 py-1.5 text-xs hover:bg-accent/50 transition-colors"
                                    onClick={() => {
                                      hook.setEditData(d => ({ ...d, assignedMembers: [...(d.assignedMembers || []), m.userId] }));
                                      hook.setMemberSearch('');
                                    }}
                                  >
                                    <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-medium text-primary">{m.displayName[0]}</div>
                                    <span className="font-medium">{m.displayName}</span>
                                    <span className="text-muted-foreground">{m.email}</span>
                                    {m.teamIds?.[0] && <span className="text-muted-foreground ml-auto">{m.teamIds[0]}</span>}
                                  </button>
                                ))}
                                {available.filter(m =>
                                  m.displayName.toLowerCase().includes(hook.memberSearch.toLowerCase()) ||
                                  m.email.toLowerCase().includes(hook.memberSearch.toLowerCase())
                                ).length === 0 && (
                                  <p className="px-2.5 py-2 text-xs text-muted-foreground">{t('positions.noMembersFound')}</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="shrink-0 flex justify-end gap-2 px-6 py-3 border-t border-border/40 bg-muted/20">
                <Button variant="outline" size="sm" onClick={() => hook.setDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button
                  size="sm"
                  disabled={hook.saving || writeBlocked}
                  onClick={() => {
                    if (!guardWrite()) return;
                    void hook.handleSave();
                  }}
                >
                  {hook.saving ? t('positions.savingState') : t('common.save')}
                </Button>
              </div>
            </div>

            {/* AI Assistant side — full screen on mobile; form returns when closed */}
            {hook.aiChatOpen && (
              <div className={jobPositionAiColumnClass}>
                {/* AI Header */}
                <div className="shrink-0 px-4 py-3 border-b border-border/40 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <span className="text-sm font-medium truncate">{t('positions.aiAssistant')}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 md:hidden"
                    onClick={() => hook.setAiChatOpen(false)}
                    aria-label={t('positions.closeAI')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Messages — Sprint 7+ follow-up: every literal here was
                    hard-coded English. Localised via `t()` so the IT
                    locale doesn't leak English copy into the panel. */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-3">
                  {hook.aiMessages.length === 0 && (
                    <div className="space-y-2 mt-2">
                      <p className="text-xs text-muted-foreground font-medium">{t('positions.aiTryAsking')}</p>
                      {[
                        { icon: '✍️', text: t('positions.aiPromptWrite') },
                        { icon: '📋', text: t('positions.aiPromptRequirements') },
                        { icon: '✨', text: t('positions.aiPromptImprove') },
                      ].map(prompt => (
                        <button
                          key={prompt.text}
                          className="flex items-center gap-2 w-full text-left text-xs p-2.5 rounded-lg border border-border/50 bg-background hover:bg-accent/50 hover:border-border transition-all"
                          onClick={() => { hook.setAiInput(prompt.text); }}
                        >
                          <span>{prompt.icon}</span>
                          <span>{prompt.text}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {hook.aiMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[88%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground rounded-br-sm whitespace-pre-wrap'
                          : 'bg-background border border-border/50 rounded-bl-sm'
                      }`}>
                        {msg.role === 'user' ? (
                          msg.text
                        ) : (
                          <div className="prose prose-xs dark:prose-invert max-w-none
                              prose-headings:font-semibold prose-headings:text-xs prose-headings:mt-2 prose-headings:mb-1
                              prose-p:my-1 prose-p:text-xs prose-p:leading-relaxed
                              prose-ul:my-1 prose-ul:pl-3 prose-li:text-xs prose-li:my-0
                              prose-ol:my-1 prose-ol:pl-3
                              prose-strong:font-semibold
                              prose-code:text-[10px] prose-code:bg-muted prose-code:px-1 prose-code:rounded
                              prose-hr:my-2">
                            <ReactMarkdown>
                              {msg.text}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {hook.aiLoading && (
                    <div className="flex justify-start">
                      <div className="bg-background border border-border/50 rounded-xl rounded-bl-sm px-3 py-2">
                        <span className="flex gap-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Suggestions Preview Panel */}
                {hook.autoFillSuggestions && (
                  <div className="mx-3 mb-2 rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-primary flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        {t('positions.aiSuggestions')}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-muted-foreground"
                        onClick={hook.dismissAutoFillSuggestions}
                      >
                        {t('positions.aiDismiss')}
                      </Button>
                    </div>
                    <div className="space-y-1 text-xs">
                      {hook.autoFillSuggestions.title && (
                        <div><span className="text-muted-foreground">{t('positions.title')}:</span> {hook.autoFillSuggestions.title}</div>
                      )}
                      {hook.autoFillSuggestions.seniority && (
                        <div><span className="text-muted-foreground">{t('positions.seniority')}:</span> {hook.autoFillSuggestions.seniority}</div>
                      )}
                      {hook.autoFillSuggestions.contractType && (
                        <div><span className="text-muted-foreground">{t('positions.contract')}:</span> {hook.autoFillSuggestions.contractType}</div>
                      )}
                      {hook.autoFillSuggestions.workMode && (
                        <div><span className="text-muted-foreground">{t('positions.workMode')}:</span> {hook.autoFillSuggestions.workMode}</div>
                      )}
                      {normalizeJobLocation(hook.autoFillSuggestions.location) && (
                        <div><span className="text-muted-foreground">{t('positions.location')}:</span> {normalizeJobLocation(hook.autoFillSuggestions.location)}</div>
                      )}
                      {(hook.autoFillSuggestions.salaryMin || hook.autoFillSuggestions.salaryMax) && (
                        <div>
                          <span className="text-muted-foreground">{t('positions.salary')}:</span>{' '}
                          {hook.autoFillSuggestions.salaryMin && hook.autoFillSuggestions.salaryMax
                            ? `${hook.autoFillSuggestions.salaryMin.toLocaleString()} – ${hook.autoFillSuggestions.salaryMax.toLocaleString()}`
                            : hook.autoFillSuggestions.salaryMin?.toLocaleString() ?? hook.autoFillSuggestions.salaryMax?.toLocaleString()
                          }
                          {hook.autoFillSuggestions.salaryCurrency && ` ${hook.autoFillSuggestions.salaryCurrency}`}
                        </div>
                      )}
                      {hook.autoFillSuggestions.description && (
                        <div>
                          <div className="text-muted-foreground mb-0.5">{t('positions.description')}:</div>
                          <div className="prose prose-xs max-w-none max-h-32 overflow-y-auto rounded border border-border/50 bg-background/50 p-1.5 text-xs leading-relaxed">
                            <ReactMarkdown>{hook.autoFillSuggestions.description}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      className="w-full gap-1"
                      onClick={hook.applyAutoFillSuggestions}
                    >
                      <Check className="h-3 w-3" />
                      {t('positions.aiApplySuggestions')}
                    </Button>
                  </div>
                )}

                {/* Input */}
                <div className="shrink-0 p-3 border-t border-border/40 space-y-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                  {/* Auto-Fill button */}
                  <Button
                    variant={hook.autoFillReady ? "default" : "outline"}
                    size="sm"
                    className="w-full gap-2"
                    onClick={hook.triggerAutoFill}
                    disabled={hook.autoFillLoading}
                    title={hook.autoFillReady
                      ? t('positions.aiAutoFillReadyTooltip')
                      : t('positions.aiAutoFillTooltip')}
                  >
                    {hook.autoFillLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                    {hook.autoFillLoading ? t('positions.aiAutoFillLoading') : t('positions.aiAutoFill')}
                  </Button>

                  <div className="flex gap-2 items-center bg-background rounded-lg border border-border/50 px-3 py-1.5 focus-within:ring-1 focus-within:ring-primary/50 focus-within:border-primary/50 transition-all">
                    <textarea
                      className="flex-1 text-xs bg-transparent border-0 outline-none placeholder:text-muted-foreground/60 resize-none min-h-[48px] max-h-[192px] py-0.5 overflow-y-auto"
                      placeholder={t('positions.aiAskPlaceholder')}
                      value={hook.aiInput}
                      onChange={e => hook.setAiInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          hook.sendAiMessage();
                        }
                      }}
                      rows={1}
                    />
                    <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-primary" onClick={hook.sendAiMessage} disabled={hook.aiLoading}>
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — two options: soft-cancel or hard-delete.
          v3.2 polish (22/05/26): widened to sm:max-w-xl + flex-wrap on
          footer so the 3 CTAs (Cancel + Soft + Hard) never overlap on
          mid-width screens. */}
      <Dialog open={!!hook.deleteId} onOpenChange={() => hook.setDeleteId(null)}>
        <DialogContent className="sm:max-w-xl max-w-[90vw]">
          <DialogHeader>
            <DialogTitle className="text-center">{t('positions.deletePosition')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground text-center">{t('positions.deleteConfirm')}</p>
          <div className="space-y-2 mt-1">
            <div className="rounded-md border border-border/50 p-3">
              <p className="text-xs font-medium mb-0.5">{t('positions.softDeleteTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('positions.softDeleteDesc')}</p>
            </div>
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <p className="text-xs font-medium text-destructive mb-0.5">{t('positions.hardDeleteTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('positions.hardDeleteDesc')}</p>
            </div>
          </div>
          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end pt-2">
            <Button variant="ghost" size="sm" onClick={() => hook.setDeleteId(null)}>{t('common.cancel')}</Button>
            <Button variant="outline" size="sm" onClick={() => {
              if (!guardWrite() || !hook.deleteId) return;
              void hook.handleDelete(hook.deleteId);
            }}>
              {t('positions.softDeleteAction')}
            </Button>
            <Button variant="destructive" size="sm" onClick={() => {
              if (!guardWrite() || !hook.deleteId) return;
              void hook.handlePermanentDelete(hook.deleteId);
            }}>
              {t('positions.hardDeleteAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobPositionsPage;
